import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  Inject,
  ElementRef,
  Input,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  AfterViewInit,
  Renderer2,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform, PopoverController } from '@ionic/angular';
import { ModalController } from '@ionic/angular';
import { SocialAuthService } from 'angularx-social-login';
import { GlobalDataService } from 'src/app/services/global-data.service';
import { MenuItem } from 'src/app/types/menu';
import { UserInterface } from 'src/app/types/user';
import { environment } from 'src/environments/environment';
import { NavigationDropdownComponent } from '../navigation-dropdown/navigation-dropdown.component';
import { injectStyles } from 'shadow-dom-inject-styles';
import { AuthService } from 'src/app/auth/auth.service';
import { SearchComponent } from '../search/search.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, AfterViewInit {
  desktop: boolean = false;
  cordova: boolean = false;
  isBrowser: boolean = false;
  logo: string;
  logoClass: string;
  mallName: string;
  openingTime: string;
  menus: MenuItem[] = [];
  ssoProfile;
  user: UserInterface;
  loginSource: 'social' | 'storage';
  checkSSO: boolean = true;
  popoverVisible: boolean = false;
  loginStateChecked: boolean = false;

  profileLinks: MenuItem[] = [
    {
      title: 'My Profile',
      path: '/profile',
    },
  ];

  @Input() color: string;

  @ViewChild('imageElement', { static: false }) imageElement: ElementRef;

  constructor(
    private router: Router,
    private platform: Platform,
    public popoverController: PopoverController,
    public modalController: ModalController,
    private authService: AuthService,
    private globalData: GlobalDataService,
    private socialAuthService: SocialAuthService,
    public el: ElementRef,
    route: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) platformId: string
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    this.platform.ready().then(() => {
      this.desktop =
        this.platform.is('desktop') ||
        (typeof window !== 'undefined' && window.innerWidth > 1200);
      this.cordova = this.platform.is('cordova');
    });

    this.platform.resize.subscribe(async () => {
      this.desktop =
        this.platform.is('desktop') ||
        (typeof window !== 'undefined' && window.innerWidth > 1200);
    });

    this.globalData.dataChange.subscribe((value) => {
      this.updateGlobalData(value);
    });

    this.authService.dataChange.subscribe((user) => {
      if (this.isBrowser && !this.cordova) {
        this.user = user;
      }
    });

    route.params.forEach(() => {
      this.checkStoreProfile();
    });
  }

  /**
   * Update data when global data changes
   * Set logo depending on mall
   */
  updateGlobalData(data) {
    this.logoClass = 'logo-' + environment.environment_name;

    if (data.menus?.header) {
      this.menus = data.menus.header;
    }

    if (data.general) {
      this.openingTime = data.general.openingTime;
      this.mallName = data.general.name;

      if (data.general.mallLogo) {
        this.logo = data.general.mallLogo;
      } else {
        if (
          environment.environment_name === 'canalwalk' ||
          environment.environment_name === 'dev'
        ) {
          this.logo = 'assets/logo-standalone@2x.png';
        } else {
          this.logo = `assets/logo-${environment.environment_name}@2x.png`;
        }
      }
    }

    this.user = this.authService.getUser();
  }

  /**
   * Present navigation dropdown
   */
  async presentNavigationDropdown(ev: any, list) {
    if (!this.popoverVisible) {
      this.popoverVisible = true;

      const popover = await this.popoverController.create({
        component: NavigationDropdownComponent,
        componentProps: {
          data: list,
          ssoProfile: this.ssoProfile,
        },
        cssClass: 'navigation-popover',
        event: ev,
        translucent: true,
        showBackdrop: false,
        animated: false,
      });

      popover.onDidDismiss().then(() => {
        this.popoverVisible = false;
      });

      return await popover.present();
    }
  }

  /**
   * Go to root on logo click
   */
  goHome() {
    this.router.navigate(['/']);
  }

  /**
   * Link to detail page dependant on content type
   */
  linkToPage(item) {
    let url = item.path;
    if (url.indexOf('/') < 0) url = '/page/' + item.path;

    return url;
  }

  /**
   * Log out user
   */
  logOutUser() {
    this.authService.logOut();

    if (
      this.user.social?.provider === 'facebook' ||
      this.user.social?.provider === 'google'
    )
      this.socialAuthService.signOut();

    this.user = undefined;
    this.router.navigate(['/']);
  }

  /**
   * Check for stored user profile to enable profile link
   */
  checkStoreProfile() {
    // Check for stored SSO profile
    if (this.isBrowser && !this.cordova) {
      this.user = this.authService.getUser();
      this.loginStateChecked = true;
    } else {
      this.loginStateChecked = true;
    }
  }

  /**
   * Toggle search modal
   */
  async presentModal() {
    const modal = await this.modalController.create({
      component: SearchComponent,
      cssClass: 'fullscreen',
    });
    return await modal.present();
  }

  ngOnInit() {
    const initialMallData = this.globalData.getGlobalData();
    if (initialMallData) this.updateGlobalData(initialMallData);

    if (this.isBrowser) {
      const toolbar = this.el.nativeElement.querySelector(
        'ion-header > ion-toolbar'
      ) as HTMLElement;
      const toolBartyles = `
        .toolbar-container {
          contain: unset !important;
          overflow: visible !important;
        }
      `;
      injectStyles(toolbar, '.toolbar-container', toolBartyles);

      const title = this.el.nativeElement.querySelector(
        'ion-header > ion-toolbar ion-title'
      ) as HTMLElement;
      const titleStyles = `
        .toolbar-title {
          overflow: visible !important;
        }
      `;
      injectStyles(title, '.toolbar-title', titleStyles);
    }
  }

  ngAfterViewInit() {
    this.imageElement.nativeElement.onload = () => {
      const imgNaturalWidth = this.imageElement.nativeElement.clientWidth;
      const imgNaturalHeight = this.imageElement.nativeElement.clientHeight;

      this.renderer.setAttribute(
        this.imageElement.nativeElement,
        'width',
        `${imgNaturalWidth}`
      );

      this.renderer.setAttribute(
        this.imageElement.nativeElement,
        'height',
        `${imgNaturalHeight}`
      );
    };
  }
}
